import { PropsWithChildren, useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { useAppStore } from '../store'
import { isAnnotatorRole } from '@/types/users'
import { notifyAnnotatorBlocked } from '@/service/annotatorAccess'

/**
 * Route guard for document viewers an annotator may not open.
 *
 * Annotators may only open parsed diagrams (P&IDs and PFDs) — the graph editor
 * is their annotation surface. Every other document viewer (datasheet,
 * timeseries, PDF/document, interview, user artifact) is off-limits. This is
 * the enforcement counterpart
 * to the catalog row-click gate in DataCatalogV2: the gate prevents the
 * navigation up front for a nice UX, while this guard catches every other
 * entry point — deep links, board-preview navigations, search results — that
 * bypass the catalog click.
 *
 * Mounted INSIDE LoginProtectedRoute so the user (and role) is already
 * hydrated by the time this renders; a non-annotator (or no user) falls
 * through to the wrapped viewer unchanged.
 */
export default function AnnotatorRestrictedRoute({ children }: PropsWithChildren) {
  const role = useAppStore((s) => s.user?.role)
  const blocked = isAnnotatorRole(role)

  useEffect(() => {
    if (blocked) notifyAnnotatorBlocked()
  }, [blocked])

  if (blocked) return <Navigate to="/data-catalog" replace />
  return <>{children}</>
}
